import { ClaimDirectDepositRequest } from '@bybeam/platform-types';
import Message from '@platform-ui-common/components/Message/index.js';
import TextInput from '@platform-ui-common/components/TextInput/index.js';
import useForm, {
  defaultRequiredErrorMessage,
  requiredField,
} from '@platform-ui-common/hooks/useForm/index.js';
import ClaimRoutes from '@platform-ui/claim/ClaimRoutes.js';
import ClaimForm from '@platform-ui/claim/components/ClaimForm/index.js';
import useClaim from '@platform-ui/claim/hooks/useClaim.js';
import { makeRoute } from '@platform-ui/utilities/Routes.js';
import isAccountNumberValid from '@platform-ui/utilities/accountNumber.js';
import React from 'react';
import { useHistory } from 'react-router';

function AccountNumber(): JSX.Element {
  const { fulfillmentId, payload, setPayload } = useClaim<ClaimDirectDepositRequest>();
  const { formData, dispatch, errors, trySubmit } = useForm(
    {
      accountNumber: payload?.bankAccount?.accountNumber ?? '',
      confirmAccountNumber: payload?.bankAccount?.accountNumber ?? '',
    },
    {
      accountNumber: requiredField(
        ({ accountNumber }) => isAccountNumberValid(accountNumber),
        defaultRequiredErrorMessage('Account Number'),
      ),
      confirmAccountNumber: requiredField(
        ({ accountNumber, confirmAccountNumber }) =>
          accountNumber !== confirmAccountNumber ? 'Numbers do not match' : undefined,
        defaultRequiredErrorMessage('Confirm Account Number'),
      ),
    },
  );

  const history = useHistory();
  const onSubmit = (): void =>
    trySubmit(() => {
      setPayload({
        ...payload,
        bankAccount: { ...payload?.bankAccount, accountNumber: formData.accountNumber },
      });
      history.push(makeRoute(ClaimRoutes.REVIEW, { fulfillmentId }));
    });
  const errorMessages = Object.values(errors).flat();

  return (
    <ClaimForm
      header="Enter your account number"
      subHeader="Please double check that your account number matches the number provided by your bank."
      helperText={
        <Message variant="info">
          Entering an incorrect account number will prevent funds from transferring to your account.
        </Message>
      }
      onSubmit={onSubmit}
      errors={errorMessages}
    >
      <TextInput
        id="account-number"
        label="Account Number"
        value={formData.accountNumber}
        onChange={(value): void =>
          dispatch({
            type: 'simpleUpdate',
            payload: { field: 'accountNumber', value },
          })
        }
        onPaste={(e: React.ChangeEvent<HTMLInputElement>): void => e.preventDefault()}
        error={!!errors?.accountNumber}
      />
      <TextInput
        id="confirm-account-number"
        label="Confirm Account Number"
        value={formData.confirmAccountNumber}
        onChange={(value): void =>
          dispatch({
            type: 'simpleUpdate',
            payload: { field: 'confirmAccountNumber', value },
          })
        }
        onPaste={(e: React.ChangeEvent<HTMLInputElement>): void => e.preventDefault()}
        error={!!errors?.confirmAccountNumber}
      />
    </ClaimForm>
  );
}

export default AccountNumber;
